/**
 * Модалка вибору активів у документи НА (рішення власника 2026-08-31):
 * МУЛЬТИВИБІР чекбоксами + фільтри «вид активу» / «підрозділ» / «МВО» +
 * пошук за назвою/інв.№. Використовують редактори введення в експлуатацію,
 * списання, внутрішнього переміщення (і продажу ОЗ — той самий канон).
 * Кандидати вже відфільтровані редактором за припустимими статусами.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { departmentsApi, employees as employeesApi } from '@/api/hr';
import { ASSET_KIND_UA } from '@/api/assetsRegistry';
import type { AssetKind } from '@/api/assets';
import TchTable from '@/shared/components/TchTable';

export type PickerCand = {
  kind: AssetKind;
  a: {
    id: string; inventory_number: string; name: string; primary_cost: string;
    department_id?: string | null; responsible_employee_id?: string | null;
  };
};

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AssetPickerModal<T extends PickerCand>({
  orgId, candidates, used, onAdd, onClose, kinds,
}: {
  orgId: string;
  candidates: T[];
  used: Set<string>;
  onAdd: (list: T[]) => void;
  onClose: () => void;
  /** Припустимі види (продаж — лише ОЗ); без прапорця — усі 3. */
  kinds?: AssetKind[];
}) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | AssetKind>('');
  const [deptId, setDeptId] = useState('');
  const [empId, setEmpId] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const depsQ = useQuery({
    queryKey: ['departments-items', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 500 }).then((r) => r.items),
    enabled: !!orgId,
  });
  const empsQ = useQuery({
    queryKey: ['employees-items', orgId],
    queryFn: () => employeesApi.list({ organization_id: orgId, limit: 500 }).then((r) => r.items),
    enabled: !!orgId,
  });
  const deps = Array.isArray(depsQ.data) ? depsQ.data : [];
  const emps = Array.isArray(empsQ.data) ? empsQ.data : [];
  const depName = (id?: string | null) => deps.find((x) => x.id === id)?.name ?? '';
  const empName = (id?: string | null) => emps.find((x) => x.id === id)?.full_name ?? '';

  const kindOptions = kinds ?? (['FIXED_ASSET', 'INTANGIBLE', 'LOW_VALUE'] as AssetKind[]);
  const list = useMemo(() => candidates.filter(({ kind: k, a }) => !used.has(a.id)
    && (!kind || k === kind)
    && (!deptId || a.department_id === deptId)
    && (!empId || a.responsible_employee_id === empId)
    && (!q || `${a.inventory_number} ${a.name}`.toLowerCase().includes(q.toLowerCase()))),
  [candidates, used, kind, deptId, empId, q]);

  const toggle = (id: string) => setChecked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const visibleChecked = list.filter((c) => checked.has(c.a.id));
  const allVisibleChecked = list.length > 0 && visibleChecked.length === list.length;
  const toggleAll = () => setChecked((s) => {
    const n = new Set(s);
    if (allVisibleChecked) list.forEach((c) => n.delete(c.a.id));
    else list.forEach((c) => n.add(c.a.id));
    return n;
  });
  // Додаємо все відмічене (навіть якщо фільтр зараз ховає частину рядків).
  const selected = candidates.filter((c) => checked.has(c.a.id) && !used.has(c.a.id));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div className="card" style={{ width: 920, maxHeight: '85vh', margin: 0,
                                     display: 'flex', flexDirection: 'column' }}
           onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Вибір активів</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Пошук (назва / інв. №)</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Вид активу</label>
            <select value={kind} onChange={(e) => setKind(e.target.value as '' | AssetKind)}>
              <option value="">усі</option>
              {kindOptions.map((k) => <option key={k} value={k}>{ASSET_KIND_UA[k]}</option>)}
            </select></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Підрозділ</label>
            <select value={deptId} onChange={(e) => setDeptId(e.target.value)}>
              <option value="">усі</option>
              {deps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>МВО (працівник)</label>
            <select value={empId} onChange={(e) => setEmpId(e.target.value)}>
              <option value="">усі</option>
              {emps.map((x) => <option key={x.id} value={x.id}>{x.full_name ?? x.id}</option>)}
            </select></div>
        </div>
        <div style={{ flex: 1, minHeight: 120, overflowY: 'auto',
                      border: '1px solid var(--border-soft)', borderRadius: 6 }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 30, textAlign: 'center' }}>
                  <input type="checkbox" checked={allVisibleChecked} onChange={toggleAll}
                         title="відмітити всі відфільтровані" />
                </th>
                <th style={{ width: 90 }}>Вид</th>
                <th style={{ width: 110 }}>Інв. №</th>
                <th style={{ textAlign: 'left' }}>Назва</th>
                <th style={{ width: 150 }}>Підрозділ</th>
                <th style={{ width: 170 }}>МВО</th>
                <th style={{ width: 110, textAlign: 'right' }}>Первісна</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={7} style={{ color: 'var(--muted)' }}>
                  Немає активів за фільтром (або всі вже додані у документ).
                </td></tr>
              )}
              {list.map((c) => (
                <tr key={c.a.id} style={{ cursor: 'pointer' }} onClick={() => toggle(c.a.id)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={checked.has(c.a.id)} readOnly />
                  </td>
                  <td>{ASSET_KIND_UA[c.kind]}</td>
                  <td>{c.a.inventory_number}</td>
                  <td>{c.a.name}</td>
                  <td style={{ color: 'var(--muted)' }}>{depName(c.a.department_id)}</td>
                  <td style={{ color: 'var(--muted)' }}>{empName(c.a.responsible_employee_id)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(c.a.primary_cost)}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
          <span style={{ color: 'var(--muted)' }}>обрано {selected.length}</span>
          <button className="primary" style={{ marginLeft: 'auto' }} disabled={selected.length === 0}
                  onClick={() => { onAdd(selected); onClose(); }}>
            додати обрані
          </button>
          <button onClick={onClose}>скасувати</button>
        </div>
      </div>
    </div>
  );
}
